<template>
  <div class="satisfaction-survey">
    <LogoLoading v-show="loading" />
    <div class="top-img" :class="{ loaded: imageLoaded }" v-if="contactImage">
      <img :src="contactImage" class="top-bg" @load="onImageLoad" />
    </div>
    <!-- Token Validating or Checking Submission -->
    <div v-if="(tokenValidating || !checkCompleted) && reservationId && !alreadySubmitted" class="container mt-5 mb-5">
      <div class="row justify-content-center">
        <div class="col-lg-8 col-md-12">
          <div class="survey-wrapper text-center">
            <LogoLoading />
            <p class="mt-3">{{ $t('satisfactionSurvey.validatingToken') || 'A validar acesso...' }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Already Submitted (priority - show this first) -->
    <div v-else-if="alreadySubmitted && reservationId && checkCompleted" class="container mt-5 mb-5">
      <div class="row justify-content-center">
        <div class="col-lg-8 col-md-12">
          <div class="survey-wrapper already-submitted">
            <div class="text-center mb-4">
              <i class="fas fa-check-circle" style="font-size: 4rem; color: #28a745;"></i>
            </div>
            <h2 class="text-center mb-4">{{ $t('satisfactionSurvey.alreadySubmitted') || 'Inquérito Já Submetido' }}</h2>
            <p class="text-center text-muted">
              {{ $t('satisfactionSurvey.alreadySubmittedMessage') || 'Obrigado! Este inquérito já foi submetido anteriormente.' }}
            </p>
            <div class="text-center mt-4">
              <button @click="$router.push('/')" class="btn btn-return-home">
                {{ $t('satisfactionSurvey.returnHome') || 'Voltar à Página Inicial' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Token Invalid -->
    <div v-else-if="reservationId && !tokenValid && !tokenValidating" class="container mt-5 mb-5">
      <div class="row justify-content-center">
        <div class="col-lg-8 col-md-12">
          <div class="survey-wrapper already-submitted">
            <div class="text-center mb-4">
              <i class="fas fa-exclamation-triangle" style="font-size: 4rem; color: #dc3545;"></i>
            </div>
            <h2 class="text-center mb-4">{{ $t('satisfactionSurvey.invalidToken') || 'Link Inválido' }}</h2>
            <p class="text-center text-muted">
              {{ $t('satisfactionSurvey.invalidTokenMessage') || 'Este link de inquérito não é válido ou expirou. Por favor, utilize o link enviado no email.' }}
            </p>
            <div class="text-center mt-4">
              <button @click="$router.push('/')" class="btn btn-return-home">
                {{ $t('satisfactionSurvey.returnHome') || 'Voltar à Página Inicial' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Form (only if token valid and not submitted and check completed) -->
    <div v-else-if="!alreadySubmitted && checkCompleted && !loading && questions.length > 0" class="container mt-5 mb-5">
      <div class="row justify-content-center">
        <div class="col-lg-8 col-md-12">
          <div class="survey-wrapper">
            <h2 class="mb-4">{{ $t('satisfactionSurvey.title') }}</h2>
            <p class="mb-4 text-muted">{{ $t('satisfactionSurvey.subtitle') }}</p>
            
             <form @submit.prevent="handleSubmit" ref="surveyForm">
              <div v-for="(question, index) in sortedQuestions" :key="question.id" class="question-item mb-4">
                <div class="question-header">
                  <label class="question-label">
                    <span class="question-number">{{ index + 1 }}.</span>
                    {{ getQuestionText(question) }}
                  </label>
                </div>
                
                 <!-- Rating Type -->
                 <div v-if="question.type === 0 || question.type === 'Rating'" class="question-answer mt-3">
                   <div class="rating-stars">
                     <button
                       type="button"
                       v-for="rating in 5"
                       :key="rating"
                       class="star-btn"
                       :class="{ active: answers[question.id] && answers[question.id] >= rating }"
                       @click="setRating(question.id, rating)"
                       :aria-label="`Rate ${rating} out of 5`"
                     >
                       <span class="star-icon">★</span>
                     </button>
                   </div>
                   <div class="rating-labels mt-2">
                     <span class="label-left">{{ $t('satisfactionSurvey.veryUnsatisfied') }}</span>
                     <span class="label-right">{{ $t('satisfactionSurvey.verySatisfied') }}</span>
                   </div>
                 </div>
                
                <!-- Open Ended Type -->
                <div v-else-if="question.type === 1 || question.type === 'OpenEnded'" class="question-answer mt-3">
                  <textarea
                    v-model="answers[question.id]"
                    class="form-control"
                    rows="4"
                    :placeholder="$t('satisfactionSurvey.typeYourAnswer')"
                  ></textarea>
                </div>
              </div>
              
              <div class="form-actions mt-5">
                <button type="submit" class="btn btn-submit btn-lg" :disabled="submitting || !isFormCompleted">
                  <span v-show="!submitting">{{ $t('satisfactionSurvey.submit') }}</span>
                  <span v-show="submitting" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  <span v-show="submitting" class="sr-only">{{ $t('loading') }}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
    
    <!-- No Questions (only show if not already submitted and not validating) -->
    <div v-if="!loading && !alreadySubmitted && checkCompleted && questions.length === 0 && !tokenValidating" class="container mt-5 mb-5">
      <div class="row justify-content-center">
        <div class="col-lg-8 col-md-12">
          <div class="alert alert-info">
            {{ $t('satisfactionSurvey.noQuestions') }}
          </div>
        </div>
      </div>
    </div>
    
    <!-- Custom Modal for Success/Error Messages -->
    <div v-if="showModal" class="custom-modal-overlay" @click.self="closeModal">
      <div class="custom-modal">
        <div class="custom-modal-header" :class="{ 'success': modalType === 'success', 'error': modalType === 'error' }">
          <h4 class="custom-modal-title">
            <span v-if="modalType === 'success'">✓</span>
            <span v-else>✗</span>
            {{ modalTitle }}
          </h4>
          <button class="custom-modal-close" @click="closeModal">&times;</button>
        </div>
        <div class="custom-modal-body">
          <p>{{ modalMessage }}</p>
        </div>
        <div class="custom-modal-footer">
          <button class="btn btn-modal" :class="{ 'btn-modal-success': modalType === 'success', 'btn-modal-error': modalType === 'error' }" @click="closeModal">OK</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex";
import { API_HELPER } from "@/helpers/api.js";
import LogoLoading from "@/components/LogoLoading.vue";
import InputGetList from "@/classes/InputGetList";

export default {
  name: 'SatisfactionSurvey',
  components: {
    LogoLoading,
  },
  metaInfo() {
    return {
      title: this.$t("satisfactionSurvey.metaTitle"),
      titleTemplate: "%s - Quinta Do Barbusano",
      htmlAttrs: {
        lang: this.$i18n.locale,
      },
      meta: [
        { name: "description", content: this.$t("satisfactionSurvey.metaDescription")},
      ],
    };
  },
  data() {
    return {
      loading: true,
      submitting: false,
      answers: {},
      reservationId: null,
      token: null,
      tokenValid: false,
      tokenValidating: true,
      alreadySubmitted: false,
      submittedAnswerId: null,
      imageLoaded: false,
      showModal: false,
      modalType: 'success', // 'success' or 'error'
      modalTitle: '',
      modalMessage: '',
      checkCompleted: false, // Flag para saber se a verificação foi concluída
    };
  },
  computed: {
    ...mapGetters('satisfactionQuestions', ['questions', 'form']),
    ...mapState({
      alertType: (state) => state.alert.type,
      alertOutput: (state) => state.alert.output,
      herosOutput: state => state.heros.listOutput,
    }),
    sortedQuestions() {
      return [...this.questions].sort((a, b) => {
        if (a.position !== b.position) {
          return a.position - b.position;
        }
        return a.id - b.id;
      });
    },
     isFormCompleted() {
       if (!this.sortedQuestions || this.sortedQuestions.length === 0) {
         return false;
       }
       return this.sortedQuestions.every(q => {
         const answer = this.answers[q.id];
         if (q.type === 0 || q.type === 'Rating' || q.type === '0') {
           return answer !== undefined && answer !== null && answer > 0 && answer <= 5;
         } else if (q.type === 1 || q.type === 'OpenEnded' || q.type === '1') {
           return answer && typeof answer === 'string' && answer.trim().length > 0;
         }
         return false;
       });
     },
    contactImage() {
      let result = null;
      if (this.herosOutput && this.herosOutput.data && this.herosOutput.data.heroItems && this.herosOutput.data.heroItems.length) {
        const item = this.herosOutput.data.heroItems.find(obj => obj.types && obj.types.some(typeObj => typeObj.type === 'Contact'));
        result = item && item.image ? API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, item.image]) : null;
      }
      return result;
    },
  },
  async mounted() {
    // Read reservationId and token from URL parameters
    const reservationIdParam = this.$route.params.reservationId;
    const tokenParam = this.$route.query.token;
    
    if (reservationIdParam) {
      this.reservationId = parseInt(reservationIdParam);
      this.token = tokenParam || null;
      
      // Validar token antes de carregar perguntas
      await this.validateToken();
      
      if (this.tokenValid && this.token) {
        // Sempre verificar se já foi submetido quando há token válido
        await this.checkIfAlreadySubmitted();
        
        // Só carregar perguntas se NÃO foi submetido
        if (!this.alreadySubmitted) {
          await this.loadQuestions();
        } else {
          // Se já foi submetido, garantir que loading é false para mostrar mensagem
          this.loading = false;
          // Não carregar perguntas se já foi submetido
          return;
        }
      } else {
        // Token inválido ou não fornecido, não carregar perguntas
        this.loading = false;
        this.checkCompleted = true;
      }
    } else {
      // Sem reservationId, permitir acesso normal (sem token)
      this.tokenValidating = false;
      this.tokenValid = true; // Permitir acesso sem token
      this.checkCompleted = true; // Não precisa verificar se já foi submetido
      this.loadQuestions();
    }
    
    this.initializeHero();
  },
  methods: {
    ...mapActions('satisfactionQuestions', ['getQuestions']),
    ...mapActions({
      clearAlert: "alert/clear",
      getHeros: 'heros/getList',
    }),
     onImageLoad() {
       this.imageLoaded = true;
     },
     getQuestionText(question) {
       // Get question text based on current locale
       const currentLocale = this.$i18n.locale || 'pt';
       
       // Try to get from Questions dictionary (new format with translations)
       if (question.questions) {
         if (typeof question.questions === 'object' && question.questions[currentLocale]) {
           return question.questions[currentLocale];
         }
         // If it's an array or other format, try to access by key
         if (question.questions[currentLocale]) {
           return question.questions[currentLocale];
         }
       }
       
       // Fallback to main question field
       return question.question || '';
     },
     setRating(questionId, rating) {
       // If clicking the same rating that's already selected, deselect it
       if (this.answers[questionId] === rating) {
         this.$set(this.answers, questionId, null);
       } else {
         this.$set(this.answers, questionId, rating);
       }
     },
     async validateToken() {
      if (!this.reservationId || !this.token) {
        this.tokenValid = false;
        this.tokenValidating = false;
        return;
      }
      
      try {
        const { API_HELPER } = await import('@/helpers/api.js');
        const url = API_HELPER.pathJoin([
          process.env.VUE_APP_API_ROOT, 
          'SatisfactionForms', 
          'validate-token', 
          this.reservationId.toString()
        ]) + `?token=${encodeURIComponent(this.token)}`;
        
        const response = await fetch(url, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (response.ok) {
          const data = await response.json();
          this.tokenValid = data.valid;
        } else {
          this.tokenValid = false;
        }
      } catch (error) {
        console.error('Error validating token:', error);
        this.tokenValid = false;
      } finally {
        this.tokenValidating = false;
      }
    },
    async checkIfAlreadySubmitted() {
      if (!this.reservationId || !this.token) {
        this.checkCompleted = true;
        return;
      }
      
      try {
        const { API_HELPER } = await import('@/helpers/api.js');
        const url = API_HELPER.pathJoin([
          process.env.VUE_APP_API_ROOT, 
          'SatisfactionForms', 
          'check-submitted', 
          this.reservationId.toString()
        ]) + `?token=${encodeURIComponent(this.token)}`;
        
        const response = await fetch(url, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (response.ok) {
          const data = await response.json();
          this.alreadySubmitted = data.alreadySubmitted === true;
          console.log('Check if already submitted:', this.alreadySubmitted);
        } else {
          // Se não conseguir verificar, assumir que não foi submetido
          this.alreadySubmitted = false;
        }
      } catch (error) {
        console.error('Error checking submission status:', error);
        // Em caso de erro, assumir que não foi submetido para não bloquear o utilizador
        this.alreadySubmitted = false;
      } finally {
        this.checkCompleted = true;
      }
    },
    async loadQuestions() {
      this.loading = true;
      this.clearAlert();
      
      try {
        let language = null;
        
        // Use current i18n locale if no reservationId, or use reservation language if available
        if (this.reservationId) {
          try {
            const { RESERVATIONS_SERVICE } = await import('@/services/reservations.service.js');
            const reservationResponse = await RESERVATIONS_SERVICE.getReservationById(this.reservationId);
            if (reservationResponse && reservationResponse.data && reservationResponse.data.language) {
              // Convert string "pt" or "en" to enum Language (1=pt, 2=en)
              const lang = reservationResponse.data.language.toLowerCase();
              language = lang === 'pt' ? 1 : lang === 'en' ? 2 : null;
            }
          } catch (error) {
            console.warn('Could not load reservation language, using i18n locale:', error);
            // Fallback to i18n locale
            const currentLocale = this.$i18n.locale || 'pt';
            language = currentLocale === 'pt' ? 1 : 2;
          }
        } else {
          // No reservation, use current i18n locale
          const currentLocale = this.$i18n.locale || 'pt';
          language = currentLocale === 'pt' ? 1 : 2;
        }
        
        // Load questions with language filter
        const queryParams = { 
          pageNumber: 1, 
          pageSize: 1000,
          language: language
        };
        
        await this.getQuestions(queryParams);
      } catch (error) {
        console.error('Error loading questions:', error);
        this.$store.dispatch('alert/error', 'Erro ao carregar perguntas do inquérito');
      } finally {
        this.loading = false;
      }
    },
     async handleSubmit(event) {
       event.preventDefault();
       this.clearAlert();
       
       if (!this.isFormCompleted) {
         this.showCustomModal(
           'error',
           this.$t('satisfactionSurvey.formIncomplete') || 'Formulário Incompleto',
           this.$t('satisfactionSurvey.formIncomplete') || 'Por favor, responda a todas as perguntas.'
         );
         return;
       }
       
       this.submitting = true;
       
       try {
         const { satisfactionQuestionsService } = await import('@/services/satisfactionQuestions.service.js');
         
         // Ensure answers are properly formatted
         const formattedAnswers = {};
         this.sortedQuestions.forEach(q => {
           const answer = this.answers[q.id];
           if (answer !== undefined && answer !== null) {
             formattedAnswers[q.id] = answer;
           }
         });
         
        await satisfactionQuestionsService.submitAnswer(formattedAnswers, this.reservationId, this.token);
        
        // Marcar como já submetido após sucesso
        this.alreadySubmitted = true;
        this.loading = false;
        
        this.showCustomModal(
          'success',
          'Inquérito Submetido com Sucesso!',
          this.$t('satisfactionSurvey.submitSuccess') || 'Obrigado! O seu inquérito foi submetido com sucesso.'
        );
        
        // Reset form after 2 seconds - mas não redirecionar, deixar mostrar a mensagem
        setTimeout(() => {
          this.answers = {};
          this.closeModal();
          // Não redirecionar, deixar a mensagem "já submetido" aparecer
        }, 2000);
      } catch (error) {
        console.error('Error submitting survey:', error);
        
        // Verificar se é erro de duplicação ou token inválido
        const errorMessage = error?.response?.data?.message || error?.message;
        if (errorMessage && (errorMessage.includes('já foi submetido') || errorMessage.includes('Token inválido'))) {
          // Marcar como já submetido e recarregar
          this.alreadySubmitted = true;
          await this.checkIfAlreadySubmitted();
          this.showCustomModal(
            'error',
            'Inquérito Já Submetido',
            'Este inquérito já foi submetido anteriormente ou o token é inválido.'
          );
        } else {
          const errorMsg = errorMessage || this.$t('satisfactionSurvey.submitError') || 'Erro ao enviar inquérito. Por favor, tente novamente.';
          this.showCustomModal(
            'error',
            'Erro na Submissão',
            errorMsg
          );
        }
      } finally {
        this.submitting = false;
      }
     },
     showCustomModal(type, title, message) {
       this.modalType = type;
       this.modalTitle = title;
       this.modalMessage = message;
       this.showModal = true;
     },
     closeModal() {
       this.showModal = false;
       this.modalTitle = '';
       this.modalMessage = '';
     },
    initializeHero() {
      const itemsPerPage = 99;
      const currentPage = 1;
      const sorter = 1;
      const propertyName = "id";
      const searchText = '';
      var input = new InputGetList(itemsPerPage, currentPage, sorter, propertyName, searchText);
      input.visible = true;
      this.getHeros(input);
    },
  },
  watch: {
    '$route'(to, from) {
      if (to.query.formId !== from.query.formId) {
        this.loadQuestions();
      }
    },
    // Watch for language changes and reload questions
    '$i18n.locale': function(newLocale, oldLocale) {
      if (newLocale !== oldLocale && !this.loading && !this.alreadySubmitted && this.checkCompleted) {
        // Reload questions with new language
        this.loadQuestions();
      }
    }
  }
};
</script>

<style scoped>
.satisfaction-survey {
  min-height: 70vh;
}

.top-img {
  position: relative;
  width: 100%;
  height: 300px;
}

.top-img .top-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  object-fit: cover;
  width: 100%;
  height: 100%;
}

.top-img::before {
  content: "";
  width: 100%;
  height: 80%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: linear-gradient(180deg, #000, rgba(84, 84, 84, 0));
  mix-blend-mode: multiply;
  opacity: 0;
  z-index: 4;
  transition: opacity 0.5s ease;
}

.top-img.loaded::before {
  opacity: 0.45;
}

.survey-wrapper {
  background: #fff;
  padding: 2rem;
  box-shadow: 0px 21px 41px -13px rgba(0, 0, 0, 0.18);
  border-radius: 5px;
}

.question-item {
  padding: 1.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
  background: #fafafa;
}

.question-header {
  margin-bottom: 1rem;
}

.question-label {
  font-weight: 600;
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 0;
}

.question-number {
  color: var(--primary-color, #962422);
  margin-right: 0.5rem;
}

.rating-stars {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  flex-wrap: wrap;
}

.star-btn {
  background: transparent;
  border: none;
  color: #ddd;
  font-size: 2.5rem;
  cursor: pointer;
  padding: 0.5rem;
  transition: all 0.3s ease;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.star-btn .star-icon {
  display: inline-block;
  transition: all 0.3s ease;
}

.star-btn:hover .star-icon {
  color: #ffc107;
  transform: scale(1.1);
}

.star-btn.active .star-icon {
  color: #ffc107;
}

.star-btn.active:hover .star-icon {
  color: #ffc107;
  transform: scale(1.1);
}

.rating-labels {
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  color: #666;
}

.label-left {
  margin-left: 0;
}

.label-right {
  margin-right: 0;
}

.question-answer textarea {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 0.75rem;
  font-size: 1rem;
}

.question-answer textarea:focus {
  outline: none;
  border-color: var(--primary-color, #962422);
  box-shadow: 0 0 0 0.2rem rgba(150, 36, 34, 0.25);
}

.form-actions {
  text-align: center;
}

.btn-submit {
  background-color: #28a745 !important;
  border-color: #28a745 !important;
  color: white !important;
  padding: 0.75rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
}

.btn-submit:hover:not(:disabled) {
  background-color: #218838 !important;
  border-color: #218838 !important;
  color: white !important;
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #28a745 !important;
  border-color: #28a745 !important;
}

/* Custom Modal Styles */
.custom-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease;
}

.custom-modal {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideIn 0.3s ease;
}

.custom-modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.custom-modal-header.success {
  background-color: #28a745;
  color: white;
  border-radius: 8px 8px 0 0;
}

.btn-modal-success {
  background-color: #28a745 !important;
  border-color: #28a745 !important;
  color: white !important;
}

.btn-modal-success:hover {
  background-color: #218838 !important;
  border-color: #218838 !important;
  color: white !important;
}

.custom-modal-header.error {
  background-color: #dc3545;
  color: white;
  border-radius: 8px 8px 0 0;
}

.custom-modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.custom-modal-close {
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: inherit;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.custom-modal-close:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.custom-modal-body {
  padding: 1.5rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #333;
}

.custom-modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
}

.btn-modal {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 0.5rem 1.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: background-color 0.2s;
}

.btn-modal:hover {
  background-color: #0056b3;
}

.btn-modal-error {
  background-color: #dc3545 !important;
  border-color: #dc3545 !important;
  color: white !important;
}

.btn-modal-error:hover {
  background-color: #c82333 !important;
  border-color: #c82333 !important;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    transform: translateY(-50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.btn-return-home {
  background-color: var(--primary-color, #B1D1A0);
  border-color: var(--primary-color, #B1D1A0);
  color: white;
  padding: 0.75rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.btn-return-home:hover {
  background-color: #9FC08F;
  border-color: #9FC08F;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(177, 209, 160, 0.3);
}

@media (max-width: 768px) {
  .survey-wrapper {
    padding: 1rem;
  }
  
  .star-btn {
    font-size: 2rem;
  }
  
  .question-item {
    padding: 1rem;
  }
}
</style>

